import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import copilotDrawerStore, {
  DRAWER_MIN_WIDTH,
  DRAWER_MAX_WIDTH,
} from "Stores/copilotDrawerStore";
import SparkleIcon from "Components/common/SparkleIcon";
import copilotEditorStore from "Stores/copilotEditorStore";
import {
  currentSurface,
  mirrorPageContext,
} from "Pages/Dashboard/Copilot/agent/copilotContext";
import CopilotCompose from "./CopilotCompose";
import ChatThread from "./ChatThread";
import Composer from "./Composer";
import AttachReceipt from "./AttachReceipt";
import DrawerStartup from "./DrawerStartup";

/*
 * The copilot as a panel over the current page instead of a separate route --
 * the page keeps its state, its scroll and its selection while you ask about it.
 *
 * Floating overlays and dims the page; pinned shrinks .mainContent instead, so
 * both stay usable together. It is the same conversation as /copilot: ChatThread
 * and Composer read copilotStore directly, so nothing here is a second engine.
 */
const CopilotDrawer = observer((props) => {
  // Prop wins when a page still renders its own drawer; otherwise take what
  // the current page published to the store.
  const findings = (props && props.findings) || copilotDrawerStore.pageFindings;
  const { open, pinned, surface, entityId } = copilotDrawerStore;
  const width = copilotDrawerStore.effectiveWidth;
  const history = useHistory();
  const location = useLocation();
  const [dragging, setDragging] = useState(false);
  // Stays mounted through the slide-out so closing animates like opening.
  const [shown, setShown] = useState(open);
  const [recentOpen, setRecentOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  // Findings describe the PAGE, not the conversation, so they stay available
  // once a chat starts -- collapsed by default then, so the thread has room.
  const [findingsOpen, setFindingsOpen] = useState(null);
  const asideRef = useRef(null);
  const findingsRef = useRef(null);
  const recentRef = useRef(null);
  const recentBtnRef = useRef(null);
  const hasThread = copilotStore.messages.length > 0;
  // Opened from an editor's wand: the drawer is a writing surface for that
  // editor, not a page assistant. Page findings would be noise here.
  const composing = surface === "email_editor";

  // The model's read is fetched here rather than by the page, because the pages
  // are not observers -- they would never re-render when the drawer opens. Only
  // fires on open, so browsing never costs a model call.
  useEffect(() => {
    if (!open || !surface || composing) return undefined;
    let live = true;
    setInsights(null);
    setInsightsLoading(true);
    copilotStore.fetchInsights(surface, entityId).then((data) => {
      if (!live) return;
      setInsights(data);
      setInsightsLoading(false);
    });
    return () => {
      live = false;
    };
  }, [open, surface, entityId]);

  // Publish this window's page for the pop-out window, which sits at /copilot
  // and can see nothing about where the user actually is. Runs whether or not
  // the drawer is open -- the pop-out may be the only copilot on screen.
  useEffect(() => {
    mirrorPageContext();
  }, [location.pathname]);

  // The drawer outlives the page it was opened from, so its surface has to
  // follow the user. Without this, opening on Sequences and walking to
  // Contacts would keep showing (and re-fetching) the sequence's insights.
  useEffect(() => {
    if (!open || composing) return;
    if (location.pathname.startsWith("/copilot")) return;
    const next = currentSurface();
    if (
      next.surface &&
      (next.surface !== surface || next.entityId !== entityId)
    ) {
      copilotDrawerStore.openFor(next.surface, next.entityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, open]);

  // A conversation is created lazily -- opening the drawer is the first moment
  // we know the user wants one. Anything the page asked on our behalf waits in
  // pendingAutoSend until the transport is actually up; sending it straight
  // after openFor() would fire before there is a conversation to send it to.
  // Coming back from the full page: reopen where we left off and reload the
  // thread by id, because the copilot page reset the store on its way out.
  useEffect(() => {
    const pending = copilotDrawerStore.consumeReopen();
    if (!pending) return;
    copilotDrawerStore.openFor(pending.surface, pending.entityId);
    if (pending.conversationId) {
      copilotStore.loadConversation(pending.conversationId);
    }
    // Keyed on the route, not on mount: the drawer is mounted once for the whole
    // app now and never remounts, so a mount-only check ran at boot and never
    // again -- coming back from the full page left the drawer shut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Opening the drawer no longer opens a connection: the findings are computed
  // locally and most opens never become a conversation. sendUserMessage
  // connects on the first message instead.
  useEffect(() => {
    if (!open) return;
    const pending = copilotStore.consumePendingAutoSend();
    if (pending) copilotStore.sendUserMessage(pending);
  }, [open, copilotStore.pendingAutoSend]);

  // A dropdown that only closes by re-clicking the button it came from is a
  // panel, not a menu. Escape and an outside click both put it away.
  useEffect(() => {
    if (!recentOpen) return undefined;
    const onDown = (e) => {
      // The toggle button is "outside" the menu, so without this the mousedown
      // closed it and the button's own click immediately reopened it.
      if (recentBtnRef.current && recentBtnRef.current.contains(e.target))
        return;
      if (recentRef.current && !recentRef.current.contains(e.target)) {
        setRecentOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [recentOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (recentOpen) {
        setRecentOpen(false);
        return;
      }
      if (copilotDrawerStore.open) copilotDrawerStore.close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [recentOpen]);

  // Once the insights float over the thread they behave like any other
  // dropdown: clicking anywhere else -- the conversation, the composer, the
  // page behind -- puts them away. Only while a thread exists; with no thread
  // they are the drawer's own content and must not close underneath the user.
  useEffect(() => {
    if (findingsOpen !== true) return undefined;
    const onDown = (e) => {
      if (findingsRef.current && !findingsRef.current.contains(e.target)) {
        setFindingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [findingsOpen]);

  // Pinning shrinks the page rather than covering it. Driving it from a class +
  // custom property keeps the layout rule in SCSS and out of here.
  useEffect(() => {
    const root = document.body;
    // The drawer renders null on /copilot, but this effect still runs -- without
    // the same guard the page keeps a drawer-width gutter with no drawer in it.
    const on = open && pinned && !location.pathname.startsWith("/copilot");
    root.classList.toggle("sgCopilotPinned", on);
    document.documentElement.style.setProperty(
      "--sgCopilotDrawerWidth",
      on ? `${width}px` : "0px",
    );
    return () => {
      root.classList.remove("sgCopilotPinned");
      document.documentElement.style.setProperty(
        "--sgCopilotDrawerWidth",
        "0px",
      );
    };
  }, [open, pinned, width, location.pathname]);

  useEffect(() => {
    const onResize = () =>
      copilotDrawerStore.setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Leaving the page closes it. Without this the open flag outlives the page
  // and the drawer reappears by itself on the next sequence you visit, which
  // reads as auto-opening -- the one thing this is not supposed to do.
  useEffect(() => () => copilotDrawerStore.close(), []);

  // The list is fetched on demand: only the full page loaded conversations, so
  // opening Recent in the drawer always showed "No conversations yet".
  useEffect(() => {
    if (recentOpen) copilotStore.loadConversations();
  }, [recentOpen]);

  const startDrag = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const onMove = (ev) => {
      copilotDrawerStore.setWidth(window.innerWidth - ev.clientX);
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    if (open) {
      setShown(true);
      return undefined;
    }
    const t = setTimeout(() => setShown(false), 240);
    return () => clearTimeout(t);
  }, [open]);

  // Mounted app-wide now, so it has to stand down on the full copilot page --
  // otherwise the same conversation renders twice, once over the other.
  if (!shown || location.pathname.startsWith("/copilot")) return null;

  // Carry the thread across rather than starting a fresh one. conversationId is
  // the PERSISTED chat id, and persistConversation() creates it lazily on the
  // first message -- so a drawer conversation that has messages but was never
  // persisted used to land on /copilot/new and be thrown away.
  // Starts a fresh conversation without leaving the page. The full page does
  // this by routing to /copilot/new; in the drawer that would defeat the point.
  const startNewChat = async () => {
    if (copilotStore.messages.length) await copilotStore.persistConversation();
    copilotStore.reset();
    copilotStore.startConversation();
    setRecentOpen(false);
  };

  const openRecent = (id) => {
    copilotStore.loadConversation(id);
    setRecentOpen(false);
  };

  const persistedPath = async () => {
    let id = copilotStore.conversationId;
    if (!id && copilotStore.messages.length) {
      await copilotStore.persistConversation();
      id = copilotStore.conversationId;
    }
    return id ? `/copilot/${id}` : "/copilot/new";
  };

  const openFullPage = async () => {
    const path = window.location.pathname + window.location.hash;
    copilotDrawerStore.setReturnTo(path);
    copilotDrawerStore.close();
    history.push(await persistedPath());
  };

  // Pop-out window, commented out with its header button above. The drawer used
  // to close before the popup was known to exist, so a blocked popup (
  // persistedPath can hit the network first, which costs the click its popup
  // permission) took the chat with it and left nothing -- hence the `win` check.
  // const openWindow = async () => {
  //   const path = await persistedPath();
  //   const win = window.open(path, "sgCopilot", "popup=yes,width=560,height=780");
  //   if (win) copilotDrawerStore.close();
  // };

  const modelFindings = (insights && insights.findings) || [];
  // Nothing worth saying about this page means say nothing: a card reading
  // "nothing to flag here" is noise that costs a third of the panel and tells
  // the user something they can already see. The startup state below carries
  // the "ask me anything" job instead.
  const nothingToShow =
    !findings.length && !modelFindings.length && !insightsLoading;

  // Portalled out of .mainContent on purpose. Pinning makes .mainContent the
  // containing block for its fixed descendants so the whole shell reflows in
  // one rule -- and the drawer must sit OUTSIDE that box, or it would be
  // positioned against the very width it is subtracting.
  return ReactDOM.createPortal(
    <>
      <aside
        ref={asideRef}
        className={`copilotDrawer ${pinned ? "pinned" : "floating"} ${
          dragging ? "dragging" : ""
        } ${open ? "" : "closing"}`}
        style={{ width: `${width}px` }}
        aria-label="Copilot"
      >
        <div
          className="copilotDrawerResize"
          onMouseDown={startDrag}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize copilot"
        />
        <div className="copilotDrawerHead">
          <span
            className="cdTitle"
            title={copilotStore.conversationTitle || "Copilot"}
          >
            <SparkleIcon className="cdSpark" />
            <span className="cdTitleText">
              {copilotStore.conversationTitle || "Copilot"}
            </span>
          </span>
          <span className="cdSpacer" />
          <div className="cdHeadActions">
            <button
              type="button"
              className="cdIconBtn"
              onClick={startNewChat}
              disabled={copilotStore.streaming}
              title="New chat"
              aria-label="New chat"
            >
              <i className="material-icons-outlined">add</i>
            </button>
            <button
              type="button"
              ref={recentBtnRef}
              className={`cdIconBtn${recentOpen ? " isOn" : ""}`}
              onClick={() => setRecentOpen((open) => !open)}
              aria-pressed={recentOpen}
              title="Recent chats"
              aria-label="Recent chats"
            >
              <i className="material-icons-outlined">history</i>
            </button>
            {/* Pop-out window — commented out for now. Uncomment this and
                openWindow() below to bring it back.
            <button
              type="button"
              className="cdIconBtn"
              onClick={openWindow}
              title="Open in a window"
              aria-label="Open in a window"
            >
              <i className="material-icons-outlined">open_in_new</i>
            </button>
            */}
            <button
              type="button"
              className="cdIconBtn"
              onClick={openFullPage}
              title="Open in full page"
              aria-label="Open in full page"
            >
              {/* `launch` is the same glyph as the pop-out's `open_in_new`, so
                  the two actions were indistinguishable. `open_in_full` is the
                  natural pair but renders blank in this icon font version. */}
              <i className="material-icons-outlined">fullscreen</i>
            </button>
            <button
              type="button"
              className="cdIconBtn cdClose"
              onClick={() => copilotDrawerStore.close()}
              title="Close"
              aria-label="Close copilot"
            >
              <i className="material-icons-outlined">close</i>
            </button>
          </div>
        </div>

        {recentOpen && !composing && (
          <div className="cdRecent" ref={recentRef}>
            <div className="cdRecent__label">Recent</div>
            {!copilotStore.conversations.length && (
              <div className="cdRecent__empty">No conversations yet</div>
            )}
            {copilotStore.conversations.slice(0, 12).map((item) => (
              <button
                key={item.id}
                type="button"
                className={`cdRecent__item${
                  item.id === copilotStore.conversationId ? " isActive" : ""
                }`}
                onClick={() => openRecent(item.id)}
              >
                {item.title || "New conversation"}
              </button>
            ))}
          </div>
        )}

        {composing ? (
          <CopilotCompose />
        ) : (
          <div className="copilotDrawerBody">
            {(() => {
              const total = findings.length + modelFindings.length;
              const expanded = findingsOpen === true;
              if (!total && !insightsLoading) return null;
              return (
                <div
                  ref={findingsRef}
                  className={`cdFindingsWrap withThread ${expanded ? "open" : ""}`}
                >
                  <button
                    type="button"
                    className="cdFindingsToggle"
                    onClick={() => setFindingsOpen(!expanded)}
                  >
                    <SparkleIcon className="cdSpark" />
                    <span className="cdFindingsToggleLabel">
                      This page
                      {total
                        ? ` · ${total} insight${total === 1 ? "" : "s"}`
                        : ""}
                      {insightsLoading && (
                        <span className="cdFindingsPending">
                          {" "}
                          · still reading…
                        </span>
                      )}
                    </span>
                    <i className="material-icons">
                      {expanded ? "expand_less" : "expand_more"}
                    </i>
                  </button>
                  {expanded && (
                    <div className="cdFindings">
                      {findings.map((finding, index) => (
                        <div
                          className={`cdFinding ${finding.tone || "neutral"}`}
                          key={index}
                        >
                          <div className="cdFindingLabel">{finding.label}</div>
                          <div className="cdFindingText">{finding.text}</div>
                          {(finding.actions || []).length > 0 && (
                            <div className="cdFindingActions">
                              {finding.actions.map((action, i) => (
                                <button
                                  type="button"
                                  key={i}
                                  className={`cdAct ${i === 0 ? "pri" : ""}`}
                                  onClick={action.run}
                                >
                                  {action.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {insightsLoading && (
                        <div className="cdReading">
                          <div className="cdReading__label">
                            <SparkleIcon className="cdSpark" />
                            <span>Reading this page…</span>
                          </div>
                          <div className="cdFinding linear-background cdSkeleton" />
                          <div className="cdFinding linear-background cdSkeleton cdSkeleton--short" />
                        </div>
                      )}
                      {modelFindings.length > 0 && (
                        <div className="cdFindingsDivider">Deeper read</div>
                      )}
                      {modelFindings.map((finding, index) => (
                        <div className="cdFinding neutral" key={`m${index}`}>
                          <div className="cdFindingText">{finding.text}</div>
                        </div>
                      ))}
                      {(insights?.actions || []).length > 0 && (
                        <div className="cdFindingActions">
                          {insights.actions.map((a, i) => (
                            <button
                              type="button"
                              key={i}
                              className={`cdAct ${i === 0 ? "pri" : ""}`}
                              onClick={() => history.push(a.path)}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <div
              className={`cdThreadScroll${hasThread ? "" : " cdThreadScroll--startup"}`}
            >
              {hasThread ? <ChatThread /> : <DrawerStartup surface={surface} />}
            </div>
          </div>
        )}

        {!composing && (
          <div className="copilotDrawerFoot">
            <AttachReceipt />
            <Composer />
          </div>
        )}
      </aside>
    </>,
    document.body,
  );
});

export default CopilotDrawer;
export { DRAWER_MIN_WIDTH, DRAWER_MAX_WIDTH };
